import { Editor } from "@tiptap/react";
import Gapcursor from '@tiptap/extension-gapcursor';
import StarterKit from "@tiptap/starter-kit";
import { lazy, Suspense, useEffect, useMemo, useState, useCallback, useRef, useId } from "react";
import type { CSSProperties } from 'react';
import { FormFieldNode, QuizTargetLabelRequest } from "./tiptap-form-field-node";
import { FragmentAnswersNode } from "./tiptap-fragment-answers-node";
import { useManagedModuleEditor } from "../../providers/managed-module-management-provider";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { FontFamily, FontSize } from "@tiptap/extension-text-style";
import { RichToolbar } from "./rich-toolbar";
import TextAlign from "@tiptap/extension-text-align";
import { usePasteFiles } from "./image-handler";
import { 
    AudioNode, BackgroundColor, BlockClass, Callout, ClassMark, Collapsible, 
    CollapsibleBody, CollapsibleHeader, CollapsibleHeaderTitle, EditorPanel, 
    FontSizeMark, HorizontalRule, ImageNode, InlineImageNode, StyleAttributeExtension,
    TextStyle, TrailingNode, VideoNode 
} from "./editor-extensions";
import { 
    CustomTableCell, CustomTableHeader, CustomTableRow, 
    TableWithAttributes, TableCaption 
} from "./table-extension";
import { GraphNode } from "./tiptap-graph-node";
import { debounce } from "lodash";
import { IframeEditor } from "./iframe-editor";
import { FormFieldDto } from "../../general/dtos";
import { cleanOfficePaste } from "./office-paste";
import { ExternalEmbedNode } from "./external-embed";
import { FieldContainerNode } from './field-container';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { UserPromptDialog } from '../dialogs/prompt-dialog';
import { ModulePreviewViewport } from '../module-preview-viewport';
import { usePendingEdit } from '../../general/unsaved-changes-context';
import type { LayoutSource } from './layout-checks';
import { moduleLayoutSources } from './layout-checks';
import { useUnsavedActions } from '../../general/unsaved-changes-context';
import { CardGroupNode, EditorCardNode } from './card-group';
import { ColumnsNode, ColumnNode } from './columns';
import { useEditorWordCompletion } from '../word-completion-toggle';
import { FormFieldClipboard } from './form-field-clipboard';

const LayoutCheckDialog = lazy(() => import('./layout-check-dialog'));
const LivePreviewPane = lazy(() => import('./live-preview-pane'));

export interface TipTapEditorProps {
    value: string;
    onChange: (html: string) => void;
    theme: string;
}

const EDITOR_EXTENSIONS = [
    ImageNode, InlineImageNode, VideoNode, ExternalEmbedNode, AudioNode, HorizontalRule, TextStyle,
    StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] }, horizontalRule: false,
        link: { openOnClick: false, HTMLAttributes: { target: null } } }),
    StyleAttributeExtension, Color.configure({ types: ["textStyle"] }), Highlight,
    TableCaption, TableWithAttributes.configure({ resizable: false }),
    CustomTableRow, CustomTableHeader, CustomTableCell,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    FormFieldNode, FormFieldClipboard, FieldContainerNode, FragmentAnswersNode, FontFamily, FontSize, BackgroundColor, EditorPanel,
    ClassMark, BlockClass, FontSizeMark, CardGroupNode, EditorCardNode, ColumnsNode, ColumnNode, Collapsible, CollapsibleHeaderTitle,
    CollapsibleHeader, CollapsibleBody, Callout, Gapcursor, GraphNode, TrailingNode,
];

export function TipTapEditor({ value, onChange, theme }: TipTapEditorProps) {
    const [clipboardError, setClipboardError] = useState('');
    const [maximized, setMaximized] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
    const [split, setSplit] = useState(50);
    const [resizing, setResizing] = useState(false);
    const workspace = useRef<HTMLDivElement>(null);
    const previewId = useId();
    const [layoutCheckVisible, setLayoutCheckVisible] = useState(false);
    const [deviceControlsTarget, setDeviceControlsTarget] = useState<HTMLDivElement | null>(null);
    const [editor, setEditor] = useState<Editor | null>(null);
    useEditorWordCompletion(editor);
    const [quizLabelRequest, setQuizLabelRequest] = useState<QuizTargetLabelRequest | null>(null);
    const [quizLabel, setQuizLabel] = useState('');
    const { selectedFragmentKey, fragments, selectFragment, setEditor: setManagedEditor, openFieldEditor, registerEditorFlush, addFormField } = useManagedModuleEditor();
    const { flushPending } = useUnsavedActions();
    // Extension callbacks live as long as the editor; read current provider data.
    const fieldContext = useRef({ fragments, openFieldEditor, addFormField });
    fieldContext.current = { fragments, openFieldEditor, addFormField };
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const pendingChange = useRef(false);
    const emittedChange = useRef<{ editor: Editor; html: string } | null>(null);
    const imagePasteStatus = usePasteFiles(editor);

    useEffect(() => { setQuizLabelRequest(null); }, [editor, selectedFragmentKey]);

    useEffect(() => {
        if (!maximized) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previousOverflow; };
    }, [maximized]);

    useEffect(() => {
        if (!maximized) return;
        let escapeCount = 0;
        let sequenceStarted = 0;
        const restoreOnEscape = (event: KeyboardEvent) => {
            if (event.repeat) return;
            if (event.key !== 'Escape' || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
                escapeCount = 0;
                return;
            }
            const now = Date.now();
            if (escapeCount === 0 || now - sequenceStarted > 1000) {
                escapeCount = 0;
                sequenceStarted = now;
            }
            if (++escapeCount === 3) setMaximized(false);
        };
        // Keyboard events inside an iframe do not bubble to the parent document.
        const documents = new Set([document, editor?.view.dom.ownerDocument, showPreview ? previewDocument : null].filter((doc): doc is Document => !!doc));
        documents.forEach(doc => doc.addEventListener('keydown', restoreOnEscape, true));
        return () => documents.forEach(doc => doc.removeEventListener('keydown', restoreOnEscape, true));
    }, [maximized, editor, showPreview, previewDocument]);

    const debouncedEmit = useMemo(
        // The editor keeps this function for its lifetime. Resolve the current
        // callback when saving so an old fragment snapshot cannot erase new fields.
        () => debounce((source: Editor) => {
            const html = source.getHTML();
            pendingChange.current = false;
            emittedChange.current = { editor: source, html };
            onChangeRef.current(html);
            return html;
        }, 500),
        []
    );

    usePendingEdit({
        dirty: () => pendingChange.current,
        flush: () => { debouncedEmit.flush(); },
        cancel: () => { debouncedEmit.cancel(); pendingChange.current = false; },
    });

    useEffect(() => () => {
        debouncedEmit.flush();
        debouncedEmit.cancel();
    }, [debouncedEmit]);

    useEffect(() => {
        if (!editor || !selectedFragmentKey) return;
        return registerEditorFlush(() => {
            if (!pendingChange.current) return null;
            const html = debouncedEmit.flush();
            return html === undefined ? null : { fragmentKey: selectedFragmentKey, html };
        });
    }, [editor, selectedFragmentKey, registerEditorFlush, debouncedEmit]);

    const handleEditorReady = useCallback((mountPoint: HTMLElement) => {
        const configuredExtensions = EDITOR_EXTENSIONS.map(ext => {
            if (ext.name === 'formFieldClipboard') {
                return ext.configure({
                    getField: (key: string) => fieldContext.current.fragments.find(f => f.key === selectedFragmentKey)?.formFields?.find(f => f.key === key) ?? null,
                    addFields: (fields: FormFieldDto[]) => {
                        if (selectedFragmentKey) fields.forEach(field => fieldContext.current.addFormField(selectedFragmentKey, field));
                    },
                    onError: setClipboardError,
                });
            }
            if (ext.name === 'fragmentAnswers') {
                return ext.configure({ getContext: () => ({ fragments: fieldContext.current.fragments, currentKey: selectedFragmentKey }) });
            }
            if (ext.name === "formField") {
                const iframeDocument = mountPoint.ownerDocument;

                return ext.configure({
                    iframeDocument: iframeDocument,
                    onGetField: (key: string) => {
                        const fragment = fieldContext.current.fragments.find(f => f.key === selectedFragmentKey);
                        return fragment?.formFields?.find(f => f.key === key) ?? null;
                    },
                    onOpenFieldEditor: (field: FormFieldDto) => fieldContext.current.openFieldEditor(field),
                    onEditQuizLabel: (request: QuizTargetLabelRequest) => { setQuizLabel(request.label); setQuizLabelRequest(request); }
                 });
            }
            return ext;
        });

        const newEditor = new Editor({
            element: mountPoint,
            extensions: configuredExtensions,
            content: value ?? "",
            editorProps: { transformPastedHTML: cleanOfficePaste },
            onUpdate: ({ editor }) => {
                pendingChange.current = true;
                debouncedEmit(editor);
            },
        });
        setEditor(newEditor);
        setManagedEditor(newEditor);
    }, [value, debouncedEmit, setManagedEditor, selectedFragmentKey]);

    useEffect(() => {
        if (editor && !editor.isDestroyed) {
            // Field creation inserts the node before React commits the new field.
            // Refresh its placeholder once that data is available, without editing HTML.
            editor.view.dispatch(editor.state.tr.setMeta("formFieldsUpdated", true));
        }
    }, [fragments, editor]);

    useEffect(() => {
        return () => {
            if (editor) {
                editor.destroy();
                setManagedEditor(null);
            }
        };
    }, [editor, setManagedEditor]);

    useEffect(() => {
        // Only sync if the editor exists and the content has actually changed
        // The parent acknowledges our debounced snapshot. Newer edits or image
        // imports may already be present; do not rebuild the document from that echo.
        if (emittedChange.current?.editor === editor && emittedChange.current.html === value) {
            emittedChange.current = null;
            return;
        }
        if (editor && editor.getHTML() !== value) {
            editor.commands.setContent(value, {
                emitUpdate: false,
                parseOptions: { preserveWhitespace: 'full' }
            });
        }
    }, [value, editor]);

    const previewSource = useMemo(() => ({
        theme: theme || localStorage.getItem('selected-theme') || '', fragmentKey: selectedFragmentKey ?? '',
        fragments, fields: fragments.find(fragment => fragment.key === selectedFragmentKey)?.formFields ?? []
    }), [theme, selectedFragmentKey, fragments]);

    return (
        <div className={`design-editor flex flex-column flex-1 min-h-0${maximized ? ' design-editor-maximized' : ''}`}>
            {clipboardError && <div role="alert" className="p-2">
                {clipboardError} <Button label="Dismiss" text size="small" onClick={() => setClipboardError('')} />
            </div>}
            <div className="design-editor-toolbar">
                <RichToolbar editor={editor} />
                <div className="design-editor-viewport-actions">
                    <div ref={setDeviceControlsTarget} className="module-preview-device-slot" />
                    {maximized && <Button type="button" text icon={showPreview ? 'pi pi-eye-slash' : 'pi pi-eye'}
                        aria-label={showPreview ? 'Hide preview pane' : 'Show preview pane'} title={showPreview ? 'Hide preview pane' : 'Show preview pane'}
                        aria-pressed={showPreview} aria-expanded={showPreview} aria-controls={previewId}
                        onMouseDown={event => event.preventDefault()} onClick={() => setShowPreview(current => !current)} />}
                    <Button type="button" text icon={maximized ? 'pi pi-window-minimize' : 'pi pi-window-maximize'}
                        aria-label={maximized ? 'Restore editor' : 'Maximize editor'}
                        tooltip={maximized ? 'Restore original size: press Esc three times quickly (within one second).' : 'Maximize editor'}
                        tooltipOptions={{ position: 'left' }}
                        aria-pressed={maximized}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => setMaximized(current => !current)} />
                </div>
            </div>
            {layoutCheckVisible && <Suspense fallback={<span role="status">Loading layout checker…</span>}>
                <LayoutCheckDialog editor={editor ?? undefined} onClose={() => setLayoutCheckVisible(false)}
                    getSources={() => moduleLayoutSources(fragments, selectedFragmentKey, editor?.getHTML() ?? value, theme || localStorage.getItem('selected-theme') || '')}
                    onOpenFragment={key => { setLayoutCheckVisible(false); selectFragment(key); }}
                    getSource={(): LayoutSource => {
                    const current = fragments.find(fragment => fragment.key === selectedFragmentKey);
                    const html = editor?.getHTML() ?? value;
                    return { html, theme: theme || localStorage.getItem('selected-theme') || '', fragmentKey: selectedFragmentKey ?? '', fields: current?.formFields ?? [],
                        fragments: fragments.map(fragment => fragment.key === selectedFragmentKey ? { ...fragment, html } : fragment) };
                }} />
            </Suspense>}
            {imagePasteStatus && <div role="status" className="text-sm p-2">{imagePasteStatus}</div>}
            {quizLabelRequest && <UserPromptDialog visible title="Quiz item label" question="Provide an optional label to identify this quiz item in answer tables and validation results. Leave it blank to use the quiz name and item number."
                onClose={() => setQuizLabelRequest(null)} actions={[
                    <Button label="Cancel" text onClick={() => setQuizLabelRequest(null)} />,
                    <Button label="Save label" onClick={() => { quizLabelRequest.onSave(quizLabel); setQuizLabelRequest(null); }} />
                ]}>
                <label htmlFor="quiz-results-label">Label for results (optional)</label>
                <InputText id="quiz-results-label" value={quizLabel} maxLength={250} autoFocus className="w-full" onChange={event => setQuizLabel(event.target.value)} />
            </UserPromptDialog>}
            <div ref={workspace} className={`design-editor-workspace${maximized && showPreview && editor ? ' design-editor-workspace-split' : ''}${resizing ? ' design-editor-workspace-resizing' : ''}`}
                style={{ '--editor-pane-ratio': `${split}fr`, '--preview-pane-ratio': `${100 - split}fr` } as CSSProperties}>
              <div className="flex flex-column flex-1 min-h-0 editor-wrapper">
                <ModulePreviewViewport controlsTarget={deviceControlsTarget} viewportLabel="Editor viewport" compactControls preserveFocus
                    onCheckLayout={editor ? () => { flushPending(); setLayoutCheckVisible(true); } : undefined}
                    onViewportCommand={() => editor?.view.focus()}>
                    <IframeEditor
                        // Use context-provided key to guarantee stability
                        // and trigger re-mount only when the selected fragment changes
                        key={selectedFragmentKey ?? "none"}
                        theme={theme}
                        editor={editor}
                        onEditorReady={handleEditorReady}
                    />
                </ModulePreviewViewport>
              </div>
              {maximized && showPreview && editor && <>
                <div className="design-editor-divider" role="separator" aria-label="Resize editor and preview panes" tabIndex={0}
                    aria-orientation="vertical" aria-valuemin={30} aria-valuemax={70} aria-valuenow={split}
                    onPointerDown={event => { if (event.button !== 0) return; event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); setResizing(true); }}
                    onPointerMove={event => {
                        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
                        const bounds = workspace.current?.getBoundingClientRect();
                        if (bounds?.width) setSplit(Math.round(Math.max(30, Math.min(70, (event.clientX - bounds.left) / bounds.width * 100))));
                    }}
                    onPointerUp={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); setResizing(false); }}
                    onLostPointerCapture={() => setResizing(false)} onPointerCancel={() => setResizing(false)}
                    onKeyDown={event => {
                        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                        event.preventDefault();
                        setSplit(current => event.key === 'Home' ? 30 : event.key === 'End' ? 70 : Math.max(30, Math.min(70, current + (event.key === 'ArrowRight' ? 2 : -2))));
                    }} />
                <Suspense fallback={<span role="status">Loading learner preview…</span>}>
                    <LivePreviewPane id={previewId} editor={editor} onDocumentReady={setPreviewDocument} source={previewSource} />
                </Suspense>
              </>}
            </div>
        </div>
    );
}
